import React, { useEffect, useState, useCallback } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ClipboardList, Save } from 'lucide-react';

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function DailyReports() {
  const [form, setForm] = useState({ date: todayIso(), hours_worked: 0, tasks_progressed: '', blockers: '', notes: '' });
  const [reports, setReports] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/reports/daily');
      setReports(data);
    } catch (err) {
      console.error('[reports] load failed:', err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/reports/daily', { ...form, hours_worked: Number(form.hours_worked) });
      toast.success('Daily report saved');
      setForm({ date: todayIso(), hours_worked: 0, tasks_progressed: '', blockers: '', notes: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  return (
    <AppShell title="Daily Reports">
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="font-display flex items-center gap-2"><ClipboardList className="h-4 w-4" />Today’s report</CardTitle></CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={submit} data-testid="daily-report-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="daily-report-date" /></div>
                <div><Label>Hours worked</Label><Input type="number" step="0.25" min="0" max="24" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} data-testid="daily-report-hours-input" /></div>
              </div>
              <div><Label>What did you work on?</Label><Textarea rows={3} value={form.tasks_progressed} onChange={(e) => setForm({ ...form, tasks_progressed: e.target.value })} data-testid="daily-report-progress" /></div>
              <div><Label>Blockers</Label><Textarea rows={2} value={form.blockers} onChange={(e) => setForm({ ...form, blockers: e.target.value })} data-testid="daily-report-blockers-textarea" /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="daily-report-notes" /></div>
              <Button type="submit" disabled={saving} data-testid="daily-report-submit-button"><Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save Report'}</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-display">My reports</CardTitle></CardHeader>
          <CardContent data-testid="my-reports-history">
            {reports.length === 0 ? (
              <div className="text-sm text-muted-foreground" data-testid="empty-state">No reports yet. Submit your first daily report.</div>
            ) : (
              <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
                {reports.map((r) => (
                  <div key={r.report_id} className="border rounded-lg p-3" data-testid="my-reports-item">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">{r.date}</div>
                      <Badge variant="secondary">{r.hours_worked}h</Badge>
                    </div>
                    {r.tasks_progressed && <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap"><strong className="text-foreground">Progress:</strong> {r.tasks_progressed}</div>}
                    {r.blockers && <div className="text-sm text-[hsl(0_72%_40%)] mt-1 whitespace-pre-wrap"><strong>Blockers:</strong> {r.blockers}</div>}
                    {r.notes && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{r.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
